import { arrayOf, func, oneOf, string } from 'prop-types';
import React from 'react';
import { Col, FormGroup, Label, Row } from 'reactstrap';

import Association from '../../associations/Association';
import DateRange from '../../generic/DateRange';
import MultiSelect from '../../generic/MultiSelect';
import SingleSelect from '../../generic/SingleSelect';
import getInfoTooltip from '../../generic/Tooltip/get-info-tooltip';
import { campaignObjectiveOptions, pools } from '../../opportunities/constants';
import { brandShape, productShape } from '../../prop-types';
import { countryOptions } from '../../quotes/constants';
import { BUSINESS_MODELS } from '../constants';
import HELP_TEXTS from '../help-texts';

function FilterBenchmarks({
  brand,
  peerBrands,
  pool,
  objective,
  countries,
  products,
  businessModel,
  onChange,
  startDate,
  endDate,
}) {
  const inputProps = { onChange };

  return (
    <Row>
      <Col sm={4}>
        <FormGroup>
          <Label for="bm-brandId" className="required">
            Brand
          </Label>
          <Association
            id="bm-brandId"
            name="brand"
            value={brand && brand.id}
            type="brand"
            disableInactiveView
            {...inputProps}
          />
        </FormGroup>
      </Col>
      <Col sm={4}>
        <FormGroup>
          <Label for="bm-peerBrandIds">
            Peer Brands (Optional){' '}
            {getInfoTooltip(HELP_TEXTS, 'peerBrands', 'filters')}
          </Label>
          <Association
            isMulti
            id="bm-peerBrandIds"
            name="peerBrands"
            value={peerBrands.map(({ id }) => id)}
            type="brand"
            disableInactiveView
            {...inputProps}
          />
        </FormGroup>
      </Col>
      <Col sm={2}>
        <FormGroup>
          <Label for="bm-pool" className="required">
            Pool
          </Label>
          <SingleSelect
            options={pools}
            id="bm-pool"
            name="pool"
            value={pool}
            {...inputProps}
          />
        </FormGroup>
      </Col>
      <Col sm={2}>
        <FormGroup>
          <Label for="bm-objective" className="required">
            Objective
          </Label>
          <SingleSelect
            options={campaignObjectiveOptions}
            id="bm-objective"
            name="objective"
            value={objective}
            {...inputProps}
          />
        </FormGroup>
      </Col>
      <Col sm={4}>
        <FormGroup>
          <Label for="bm-products" className="required">
            Products
          </Label>
          <Association
            id="bm-products"
            name="products"
            value={products.map(product => product.id)}
            isMulti
            type="product"
            disableInactiveView
            {...inputProps}
          />
        </FormGroup>
      </Col>
      <Col sm={4}>
        <FormGroup>
          <Label for="bm-timeframe">Timeframe (Optional)</Label>
          <DateRange
            id="bm-timeframe"
            startName="startDate"
            endName="endDate"
            startDate={startDate}
            endDate={endDate}
            small
            {...inputProps}
          />
        </FormGroup>
      </Col>
      <Col sm={2}>
        <FormGroup>
          <Label for="bm-businessModel">Business Model (Optional)</Label>
          <SingleSelect
            options={BUSINESS_MODELS}
            id="bm-businessModel"
            name="businessModel"
            value={businessModel || undefined}
            isClearable
            {...inputProps}
          />
        </FormGroup>
      </Col>
      <Col sm={2}>
        <FormGroup>
          <Label for="bm-countries" className="required">
            Countries
          </Label>
          <MultiSelect
            options={countryOptions}
            id="bm-countries"
            name="countries"
            value={countries}
            {...inputProps}
          />
        </FormGroup>
      </Col>
    </Row>
  );
}

FilterBenchmarks.propTypes = {
  onChange: func.isRequired,
  brand: brandShape,
  peerBrands: arrayOf(brandShape.isRequired).isRequired,
  pool: string.isRequired,
  objective: string.isRequired,
  countries: arrayOf(string.isRequired).isRequired,
  products: arrayOf(productShape.isRequired).isRequired,
  businessModel: oneOf(BUSINESS_MODELS.map(o => o.value)),
  startDate: string,
  endDate: string,
};

FilterBenchmarks.defaultProps = {
  brand: null,
  businessModel: null,
  startDate: null,
  endDate: null,
};

export default FilterBenchmarks;
